import { styled } from '@mui/material/styles';

interface StyledTextAreaWrapperProps {
  isIdle?: boolean;
  isDisabled?: boolean;
  maxRows: number;
  isError?: boolean;
}

const getTextAreaBackgroundColor = (theme: any, isDisabled?: boolean, isIdle?: boolean, isError?: boolean) => {
  if (isError) return theme.palette.labels.bt_red_light;
  if (isDisabled) return theme.palette.grey.bt_alt_grey_light;
  if (isIdle) return theme.palette.blueLabels.bt_blue_field_empty;
  return 'transparent';
};

const getWrapperBorderColor = (theme: any, isDisabled?: boolean, isIdle?: boolean, isError?: boolean) => {
  if (isError) return theme.palette.labels.bt_red_reg;
  if (isDisabled) return theme.palette.grey.divider_grey_v1;
  if (isIdle) return theme.palette.blueLabels.bt_blue_field_empty_hover;
  return theme.palette.grey[300];
};

const getPlaceholderColor = (theme: any, isError?: boolean) => {
  if (isError) return theme.palette.labels.bt_red_reg;
  return theme.palette.blueLabels.bt_blue_chip_light;
};

const getCounterColor = (theme: any, isDisabled?: boolean, isError?: boolean) => {
  if (isError) return theme.palette.labels.bt_red_reg;
  if (isDisabled) return theme.palette.text.disable_txt_V2;
  return theme.palette.text.secondary_txt;
};

const getWrapperHoverBackgroundColor = (theme: any, isIdle?: boolean, isError?: boolean) => {
  if (isError) return theme.palette.labels.bt_red_mid;
  if (isIdle) return theme.palette.blueLabels.bt_blue_field_empty_hover;
  return 'transparent';
};

const WRAPPER_PADDING = 8;
const WRAPPER_PADDING_BOTTOM = 24;
const TEXTAREA_PADDING = 2;
const CONTENT_GAP = 4;

const BASE_HEIGHT = WRAPPER_PADDING + WRAPPER_PADDING_BOTTOM + TEXTAREA_PADDING + CONTENT_GAP;

export const StyledTextAreaWrapper = styled('div', {
  shouldForwardProp: (prop: string) => !['isIdle', 'isDisabled', 'maxRows', 'isError'].includes(prop),
})<StyledTextAreaWrapperProps>(({ theme: baseTheme, isIdle, isDisabled, maxRows, isError }) => {
  const theme = baseTheme as any;
  const textAreaBackgroundColor = getTextAreaBackgroundColor(theme, isDisabled, isIdle, isError);
  const wrapperBorderColor = getWrapperBorderColor(theme, isDisabled, isIdle, isError);
  const placeholderColor = getPlaceholderColor(theme, isError);
  const counterColor = getCounterColor(theme, isDisabled, isError);
  const wrapperHoverBackgroundColor = getWrapperHoverBackgroundColor(theme, isIdle, isError);

  return {
    borderRadius: '4px',
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: `calc(${BASE_HEIGHT}px + 1rem*${maxRows})`,
    backgroundColor: textAreaBackgroundColor,
    border: `1px solid ${wrapperBorderColor}`,
    padding: `${WRAPPER_PADDING}px ${WRAPPER_PADDING}px ${WRAPPER_PADDING_BOTTOM}px ${WRAPPER_PADDING}px`,
    cursor: isDisabled ? 'default' : 'text',

    ...(!isDisabled
      ? {
          '&:hover': {
            backgroundColor: wrapperHoverBackgroundColor,

            '& > textarea': {
              backgroundColor: wrapperHoverBackgroundColor,
            },
          },
        }
      : {}),

    '& > textarea': {
      width: '100%',
      height: '100%',
      resize: 'none',
      padding: `${TEXTAREA_PADDING}px`,
      fontFamily: theme.fonts.robotoRegular,
      backgroundColor: textAreaBackgroundColor,
      color: isDisabled ? theme.palette.text.disable_txt_V2 : theme.palette.text.txt_default,
      border: 'none!important',
      cursor: isDisabled ? 'default' : 'text',
      userSelect: isDisabled ? 'none' : 'auto',
      pointerEvents: 'auto',

      '@-moz-document url-prefix()': {
        scrollbarColor: `${theme.palette.grey[300]} transparent`,
        scrollbarWidth: 'thin',
      },

      '&::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },

      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },

      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[300],
        borderRadius: '3px',
      },

      '&::-webkit-scrollbar-button': {
        display: 'none!important',
      },

      '&:focus': {
        outline: 'none',
        border: `1px solid ${isIdle ? theme.palette.blueLabels.bt_blue_field_empty_hover : theme.palette.grey[300]}`,
      },

      '&::placeholder': {
        color: placeholderColor,
      },
    },

    '& > span#character-counter': {
      color: counterColor,
      fontSize: theme.fontSize.small,
      fontWeight: 400,
      lineHeight: '14px',
      position: 'absolute',
      bottom: '6px',
      right: '7px',
    },
  };
});
